import type { Movie } from "@/mocks/movies";

export const PAGE_SIZE = 9;

export type SortKey = "hot" | "new" | "rating";

export interface FilterState {
  region: string;
  year: string;
  genres: string[];
  sort: SortKey;
}

export const defaultFilters: FilterState = {
  region: "全部",
  year: "全部",
  genres: ["全部"],
  sort: "hot",
};

const hotScore = (m: Movie) => m.rating * 100 + (m.year - 2000);

export function sortMovies(list: Movie[], sort: SortKey): Movie[] {
  const copy = [...list];
  if (sort === "rating") {
    copy.sort((a, b) => b.rating - a.rating);
  } else if (sort === "new") {
    copy.sort((a, b) => b.year - a.year || b.updatedAt.localeCompare(a.updatedAt));
  } else {
    copy.sort((a, b) => hotScore(b) - hotScore(a));
  }
  return copy;
}

export function filterMovies(list: Movie[], filter: FilterState): Movie[] {
  const activeGenres = filter.genres.filter((g) => g !== "全部");
  const result = list.filter((m) => {
    const regionOk = filter.region === "全部" || m.region === filter.region;
    const yearOk = filter.year === "全部" || String(m.year) === filter.year;
    const genreOk = activeGenres.length === 0 || activeGenres.some((g) => m.genres.includes(g));
    return regionOk && yearOk && genreOk;
  });
  return sortMovies(result, filter.sort);
}

export function searchMovies(list: Movie[], keyword: string, category: string): Movie[] {
  const kw = keyword.trim().toLowerCase();
  let result = list;
  if (category && category !== "all") {
    result = result.filter((m) => m.category === category);
  }
  if (kw) {
    result = result.filter((m) => {
      const haystack = [
        m.title,
        m.director,
        m.region,
        ...m.actors,
        ...m.genres,
        m.synopsis,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(kw);
    });
  }
  return sortMovies(result, "hot");
}

export function paginate<T>(list: T[], page: number, pageSize: number = PAGE_SIZE) {
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    total,
    totalPages,
    page: safePage,
  };
}

export function getMovieById(list: Movie[], id: string): Movie | undefined {
  return list.find((m) => m.id === id);
}

export function getByCategory(list: Movie[], type: string): Movie[] {
  return list.filter((m) => m.category === type);
}

export function getRecommendations(current: Movie, list: Movie[], count = 6): Movie[] {
  const scored = list
    .filter((m) => m.id !== current.id)
    .map((m) => {
      const genreMatches = m.genres.filter((g) => current.genres.includes(g)).length;
      const actorMatches = m.actors.filter((a) => current.actors.includes(a)).length;
      const sameCategory = m.category === current.category ? 1 : 0;
      return { movie: m, score: genreMatches * 3 + actorMatches * 5 + sameCategory };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || b.movie.rating - a.movie.rating);
  return scored.slice(0, count).map((item) => item.movie);
}

export function formatRating(rating: number): string {
  return rating.toFixed(1);
}